/**
 * useTransactions Hook
 * ahooks data fetching hook for vendor transaction history
 */

import { useRequest } from 'ahooks';
import type { Options } from 'ahooks/lib/useRequest/src/types';
import { transactionApi } from '@/services/api';
import type {
  TransactionListResponse,
  TransactionListParams,
} from '@/types/api';

/**
 * Hook to fetch transaction history for a specific vendor
 */
export function useTransactions(
  vendorId: number | null,
  params?: Omit<TransactionListParams, 'vendorId'>,
  config?: Options<TransactionListResponse, []>
) {
  const { data, error, loading, refresh } = useRequest(
    () => transactionApi.list(vendorId!, params),
    {
      ready: !!vendorId,
      refreshDeps: [vendorId, JSON.stringify(params)],
      cacheKey: vendorId ? `/api/vendors/${vendorId}/transactions?${JSON.stringify(params)}` : undefined,
      staleTime: 60000, // 1 minute
      ...config,
    }
  );

  return {
    transactions: data?.data ?? [],
    pagination: data?.pagination,
    error,
    isLoading: loading,
    mutate: refresh,
    refresh,
  };
}

/**
 * Hook to fetch transaction history for a specific customer (#88).
 * Mirrors useTransactions — same shared param/response shapes, customer route.
 */
export function useCustomerTransactions(
  customerId: number | null,
  params?: TransactionListParams,
  config?: Options<TransactionListResponse, []>
) {
  const { data, error, loading, refresh } = useRequest(
    () => transactionApi.listForCustomer(customerId!, params),
    {
      ready: !!customerId,
      refreshDeps: [customerId, JSON.stringify(params)],
      cacheKey: customerId
        ? `/api/customers/${customerId}/transactions?${JSON.stringify(params)}`
        : undefined,
      staleTime: 60000, // 1 minute
      ...config,
    }
  );

  return {
    transactions: data?.data ?? [],
    pagination: data?.pagination,
    error,
    isLoading: loading,
    mutate: refresh,
    refresh,
  };
}

/**
 * Hook to filter transactions by type
 */
export function useTransactionsByType(
  vendorId: number | null,
  transactionType: string,
  config?: Options<TransactionListResponse, []>
) {
  return useTransactions(
    vendorId,
    {
      transactionType,
    },
    config
  );
}

/**
 * Hook to filter transactions by date range
 */
export function useTransactionsByDateRange(
  vendorId: number | null,
  startDate: string,
  endDate: string,
  config?: Options<TransactionListResponse, []>
) {
  return useTransactions(
    vendorId,
    {
      startDate,
      endDate,
    },
    config
  );
}
